
import { defineComponent, reactive, ref, PropType, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { VulnerabilityFixService, LeakTerminalList as List, LeakTerminalQueryParams } from '../../vulnerability-fix.service'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import RepairDrawer from './_components/repair-drawer.vue'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import { TerminalType, TerminalTypeOptionsTempList } from '@/views/dashboard/_enum/terminal-type'
import { useRoute } from 'vue-router'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import UnauthorizedTooltip from '@/views/dashboard/_components/unauthorized-tooltip/unauthorized-tooltip.vue'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import LoadMoreButton from '@/components/load-more-button.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'

const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth,
}

function isDisabled (row) {
  return getAuthByList(row.auth_status, ModulePermissionKey[row.area_type]) !== TerminalAuthStatus.normal
}

export default defineComponent({
  components: {
    UnauthorizedTooltip,
    IPColumn,
    RepairDrawer,
    TerminalIconName,
    SearchFilterBar,
    GroupTreeSelector,
    LoadMoreButton
  },
  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      default: TableViewMode.preview
    },
    scanRefresh: {
      type: Boolean,
      default: false
    },
    /** 抽屉内打开时预设「显示范围」（如高危漏洞风险终端 = 1），未传则沿用路由 query.range */
    initialRange: {
      type: Number as PropType<number | undefined>,
      default: undefined
    }
  },
  emits: ['viewAll'],
  setup (props, { emit }) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([VulnerabilityFixService])
    const service = injector.get(VulnerabilityFixService)

    const route = useRoute()
    const leakScanStatusI18n = computed(() => [
      t('safeProtectionModule.leakScanStatus0'),
      t('safeProtectionModule.leakScanStatus1'),
      t('safeProtectionModule.leakScanStatus2'),
      t('safeProtectionModule.leakScanStatus3'),
      t('safeProtectionModule.leakScanStatus4'),
    ])
    const authStatusLabelI18n = computed(() => ({
      0: t('safeProtectionModule.terminalAuthNormal'),
      1: t('safeProtectionModule.terminalAuthNotConfigured'),
      2: t('safeProtectionModule.terminalAuthUnsubscribed'),
    }))
    const tableData = ref<List[]>([])
    const isLoading = ref(false)
    const hasMore = ref(true)
    const total = ref(0)
    const repairDrawer = ref(false)
    const currentItem = ref<List | null>(null)

    const queryParams = reactive<LeakTerminalQueryParams>({
      group_id: -1,
      page: 1,
      limit: props.viewMode === TableViewMode.preview ? 10 : 20,
      asc: false,
      sort: '',
      keyword: '',
      range: props.initialRange ?? (Number(route.query.range) || undefined),
      system_type: undefined,
      permission_names: [ModulePermissionKey[AreaType.linux]],
    })

    const sortParams = reactive({
      asc: false,
      sort: ''
    })

    const filterFormColumns = computed<Partial<ObjectFormItem<LeakTerminalQueryParams, keyof LeakTerminalQueryParams>>>(() => ({
      group_id: {
        colOpt: { style:'flex:0 0 150px' },
        slot: true,
        label: t('safeProtectionModule.group'),
        hiddenAllLabel: true,
        outSearchFiledFlag: true,
        events: {
          change: () => getData(),
        },
      },
      range: {
        colOpt: { style: 'flex: 0 0 130px' },
        label: t('safeProtectionModule.displayRange'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        outSearchFiledFlag: true,
        dictData: [
          { label: t('safeProtectionModule.highRiskLeakTerminal'), value: 1 }
        ],
        events: {
          change: () => getData(),
        },
      },
      system_type: {
        colOpt: { style: 'flex: 0 0 130px' },
        label: t('safeProtectionModule.os'),
        type: FormType.Select,
        outSearchFiledFlag: true,
        selectOpt: { clearable: true },
        dictData: TerminalTypeOptionsTempList.filter(item => +item.value !== TerminalType.mac).map(item => ({ label: item.label, value: +item.value })),
         events: {
          change: () => getData(),
        },
      },
    }))

    function getData (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        tableData.value = []
      }

      isLoading.value = true

      service.getLeakTerminals({
        ...queryParams,
        sort: sortParams.sort,
        asc: sortParams.asc,
        range: queryParams.range || undefined,
        system_type: queryParams.system_type || undefined,
      } as LeakTerminalQueryParams).then(response => {
        if (queryParams.page === 1) {
          tableData.value = response.list
        } else {
          tableData.value = [...tableData.value, ...response.list]
        }
        total.value = response.total
        hasMore.value = tableData.value.length < total.value
      }).finally(() => {
        isLoading.value = false
      })
    }

    function handlerSort (e) {
      sortParams.asc = e.order === 'ascending'
      sortParams.sort = e.prop
      queryParams.page = 1
      getData()
    }

    function handleSearch (value: string) {
      queryParams.keyword = value
      queryParams.page = 1
      getData()
    }

    function handleQuery (form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      getData()
    }

    function handleReset () {
      queryParams.keyword = ''
      queryParams.range = props.initialRange ?? (Number(route.query.range) || undefined)
      queryParams.system_type = undefined
      queryParams.page = 1
      getData()
    }

    function updateGroupId (groupId: number) {
      if (queryParams.group_id !== groupId) {
        queryParams.group_id = groupId
        queryParams.page = 1
        getData()
      }
    }

    function loadMoreData () {
      if (!hasMore.value || isLoading.value || props.viewMode === TableViewMode.preview) return
      queryParams.page = queryParams.page! + 1
      getData(true)
    }

    function repairTerminal (item: List) {
      currentItem.value = item
      repairDrawer.value = true
    }

    function handleRepairDrawerClose () {
      repairDrawer.value = false
    }

    onMounted(() => {
      getData()
    })

    watch(() => props.scanRefresh, (nv) => {
      nv && getData()
    })

    return {
      leakScanStatusI18n,
      authStatusLabelI18n,
      TerminalTypeOptionsTempList,
      AreaType,
      TerminalType,
      TerminalAuthStatus,
      getAuthByList,
      queryParams,
      isDisabled,
      ModulePermissionKey,
      tableData,
      isLoading,
      total,
      hasMore,
      repairDrawer,
      currentItem,
      filterFormColumns,
      handlerSort,
      handleSearch,
      handleQuery,
      handleReset,
      updateGroupId,
      loadMoreData,
      getData,
      repairTerminal,
      handleRepairDrawerClose,
      TableViewMode
    }
  }
})
